import { IconChevronLeft, IconChevronRight, IconTrash } from '@tabler/icons-react'
import dayjs, { Dayjs } from 'dayjs'
import capitalize from 'lodash/capitalize'
import { useEffect, useState } from 'react'

import { AstraIcon } from '../../stories/AstraIcon/AstraIcon'
import { AstraButton } from '../../stories/Inputs/AstraButton/AstraButton'
import { AstraParagraph } from '../../stories/Typography/AstraParagraph/AstraParagraph'
import { add, day, getToday, isWeekend } from '../../utils/extendedDayjs'

const months = dayjs.monthsShort()

type Props = {
    value?: Dayjs
    onChange: (value: Dayjs | undefined) => void

    placement?: 'top' | 'right' | 'bottom' | 'left' | undefined
    allowClear?: boolean
}

export const MonthPicker = ({ value, onChange, allowClear = false }: Props) => {
    const [year, setYear] = useState(getToday().year())

    useEffect(() => {
        if (value) setYear(value.year())
    }, [value])

    const closeModal = () => {
        setYear(value?.year() ?? getToday().year())
    }

    const handleClickMonth = (month: string) => {
        const startOfMonth = day().date(1).month(months.indexOf(month)).year(year)
        const firstWeekdayOfMonth = isWeekend(startOfMonth) ? add(startOfMonth, 1, 'week').startOf('isoWeek') : startOfMonth

        onChange(firstWeekdayOfMonth)
        closeModal()
    }

    const setToToday = () => {
        const today = getToday()
        onChange(today)
        setYear(today.year())
        closeModal()
    }

    const clearDate = () => {
        onChange(undefined)
        closeModal()
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between space-x-2 px-4 pb-1 pt-2">
                <AstraButton variant="secondary" onClick={() => setYear(year - 1)} icon={<AstraIcon icon={IconChevronLeft} />} />
                <AstraParagraph weight="strong" variant="body-large">
                    {year}
                </AstraParagraph>
                <AstraButton variant="secondary" onClick={() => setYear(year + 1)} icon={<AstraIcon icon={IconChevronRight} />} />
            </div>
            <div className="p-1">
                <div className="grid grid-cols-3 grid-rows-4 gap-x-1 gap-y-2">
                    {months.map(month => {
                        const isCurrentMonth = value?.month() === months.indexOf(month) && value.year() === year

                        return (
                            <AstraButton
                                key={month}
                                fullWidth
                                onClick={() => handleClickMonth(month)}
                                variant={isCurrentMonth ? 'primary' : 'ghost'}
                                label={capitalize(month)}
                            />
                        )
                    })}
                </div>
            </div>
            <div className="flex justify-evenly gap-2 px-2 pb-2 pt-1">
                <AstraButton onClick={setToToday} fullWidth label="I dag" variant="secondary" />
                {allowClear && (
                    <AstraButton
                        className="justify-center"
                        icon={<AstraIcon icon={IconTrash} />}
                        fullWidth
                        variant="critical-secondary"
                        onClick={clearDate}
                        label="Fjern"
                    />
                )}
            </div>
        </div>
    )
}
